import React, { useState } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Space, 
  Tag, 
  Input, 
  Select, 
  Modal,
  Form,
  message,
  Typography,
  Tooltip,
  Popconfirm,
  Descriptions,
  Switch,
  InputNumber
} from 'antd';
import { 
  SettingOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  SearchOutlined,
  FilterOutlined,
  DeleteOutlined,
  CopyOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import dayjs from 'dayjs';
import { useAuthStore } from '../../stores/authStore';

const { Search } = Input;
const { Option } = Select;
const { Title, Text } = Typography;
const { TextArea } = Input;

/**
 * 系统参数管理页面组件
 */
const SystemParameterManagement = () => {
  const { user: currentUser } = useAuthStore();
  const queryClient = useQueryClient();
  
  // 状态管理
  const [searchText, setSearchText] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState('');
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [selectedParameter, setSelectedParameter] = useState(null);
  const [editingParameter, setEditingParameter] = useState(null);
  
  // 表单实例
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  /**
   * 获取系统参数列表
   */
  const { data: parametersData, isLoading } = useQuery(
    ['systemParameters', searchText, categoryFilter, visibilityFilter],
    async () => {
      const params = {
        page: 1,
        limit: 50
      };
      
      if (searchText) params.search = searchText;
      if (categoryFilter) params.category = categoryFilter;
      if (visibilityFilter !== '') params.isVisible = visibilityFilter;
      
      const response = await axios.get('/system-parameters', { params });
      return response.data.data;
    },
    {
      keepPreviousData: true,
      onError: (error) => {
        message.error('获取系统参数列表失败: ' + (error.response?.data?.message || error.message));
      }
    }
  );

  /**
   * 获取参数分类列表
   */
  const { data: categoriesData } = useQuery(
    'parameterCategories',
    async () => {
      const response = await axios.get('/system-parameters/categories');
      return response.data.data;
    },
    {
      onError: (error) => {
        console.error('获取参数分类失败:', error);
      }
    }
  );

  /**
   * 创建系统参数
   */
  const createParameterMutation = useMutation(
    async (parameterData) => {
      const response = await axios.post('/system-parameters', parameterData);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('系统参数创建成功');
        setIsCreateModalVisible(false);
        createForm.resetFields();
        queryClient.invalidateQueries('systemParameters');
        queryClient.invalidateQueries('parameterCategories');
      },
      onError: (error) => {
        message.error('创建系统参数失败: ' + (error.response?.data?.message || error.message));
      }
    }
  );

  /**
   * 更新系统参数
   */
  const updateParameterMutation = useMutation(
    async ({ id, ...parameterData }) => {
      const response = await axios.put(`/system-parameters/${id}`, parameterData);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('系统参数更新成功');
        setIsEditModalVisible(false);
        editForm.resetFields();
        setEditingParameter(null);
        queryClient.invalidateQueries('systemParameters');
        queryClient.invalidateQueries('parameterCategories');
      },
      onError: (error) => {
        message.error('更新系统参数失败: ' + (error.response?.data?.message || error.message));
      }
    }
  );

  /**
   * 删除系统参数
   */
  const deleteParameterMutation = useMutation(
    async (id) => {
      const response = await axios.delete(`/system-parameters/${id}`);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('系统参数删除成功');
        queryClient.invalidateQueries('systemParameters');
        queryClient.invalidateQueries('parameterCategories');
      },
      onError: (error) => {
        message.error('删除系统参数失败: ' + (error.response?.data?.message || error.message));
      }
    }
  );

  /**
   * 获取参数类型标签颜色
   */
  const getTypeColor = (type) => {
    const colors = {
      string: 'blue',
      number: 'green',
      boolean: 'orange',
      json: 'purple',
      array: 'cyan'
    };
    return colors[type] || 'default';
  };

  /**
   * 获取参数类型显示文本
   */
  const getTypeText = (type) => {
    const texts = {
      string: '字符串',
      number: '数字',
      boolean: '布尔值',
      json: 'JSON',
      array: '数组'
    };
    return texts[type] || type;
  };

  /**
   * 格式化参数值显示
   */
  const formatParameterValue = (value, type) => {
    if (value === null || value === undefined) return '-';
    
    switch (type) {
      case 'boolean':
        return value ? '是' : '否';
      case 'json':
      case 'array':
        try {
          return JSON.stringify(JSON.parse(value), null, 2);
        } catch {
          return value;
        }
      default:
        return String(value);
    }
  };

  /**
   * 处理查看参数详情
   */
  const handleViewParameter = (parameter) => {
    setSelectedParameter(parameter);
    setIsDetailModalVisible(true);
  };

  /**
   * 处理编辑参数
   */
  const handleEditParameter = (parameter) => {
    setEditingParameter(parameter);
    setIsEditModalVisible(true);
    
    // 设置表单初始值
    setTimeout(() => {
      editForm.setFieldsValue({
        paramKey: parameter.paramKey,
        paramValue: parameter.paramValue,
        paramType: parameter.paramType,
        description: parameter.description,
        category: parameter.category,
        isEditable: parameter.isEditable,
        isVisible: parameter.isVisible,
        sortOrder: parameter.sortOrder
      });
    }, 100);
  };

  /**
   * 处理删除参数
   */
  const handleDeleteParameter = (parameter) => {
    deleteParameterMutation.mutate(parameter.id);
  };

  /**
   * 处理创建参数
   */
  const handleCreateParameter = () => {
    setIsCreateModalVisible(true);
    setTimeout(() => {
      createForm.resetFields();
      createForm.setFieldsValue({
        paramType: 'string',
        isEditable: true,
        isVisible: true,
        sortOrder: 0
      });
    }, 100);
  };

  /**
   * 处理创建表单提交
   */
  const handleCreateSubmit = async (values) => {
    try {
      // 数据类型转换
      const parameterData = {
        ...values,
        sortOrder: parseInt(values.sortOrder) || 0
      };
      
      createParameterMutation.mutate(parameterData);
    } catch (error) {
      message.error('表单数据处理失败');
    }
  };

  /**
   * 处理编辑表单提交
   */
  const handleEditSubmit = async (values) => {
    try {
      // 数据类型转换
      const parameterData = {
        id: editingParameter.id,
        ...values,
        sortOrder: parseInt(values.sortOrder) || 0
      };
      
      updateParameterMutation.mutate(parameterData);
    } catch (error) {
      message.error('表单数据处理失败');
    }
  };

  /**
   * 复制参数值到剪贴板
   */
  const handleCopyValue = async (value) => {
    try {
      await navigator.clipboard.writeText(String(value));
      message.success('参数值已复制到剪贴板');
    } catch (error) {
      message.error('复制失败');
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '参数键',
      dataIndex: 'paramKey',
      key: 'paramKey',
      width: 200,
      fixed: 'left',
      render: (key) => (
        <Text code copyable={{ text: key }}>
          {key}
        </Text>
      ),
    },
    {
      title: '参数值',
      dataIndex: 'paramValue',
      key: 'paramValue',
      width: 200,
      render: (value, record) => (
        <div style={{ maxWidth: 180 }}>
          <Text 
            ellipsis={{ tooltip: formatParameterValue(value, record.paramType) }}
            style={{ cursor: 'pointer' }}
            onClick={() => handleCopyValue(value)}
          >
            {formatParameterValue(value, record.paramType)}
          </Text>
          <Tooltip title="复制参数值">
            <Button 
              type="text" 
              size="small" 
              icon={<CopyOutlined />}
              onClick={() => handleCopyValue(value)}
              style={{ marginLeft: 4 }}
            />
          </Tooltip>
        </div>
      ),
    },
    {
      title: '类型',
      dataIndex: 'paramType',
      key: 'paramType',
      width: 80,
      render: (type) => (
        <Tag color={getTypeColor(type)}>
          {getTypeText(type)}
        </Tag>
      ),
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (category) => (
        <Tag color="blue">{category}</Tag>
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      render: (description) => (
        <Text ellipsis={{ tooltip: description }}>
          {description || '-'}
        </Text>
      ),
    },
    {
      title: '可编辑',
      dataIndex: 'isEditable',
      key: 'isEditable',
      width: 80,
      render: (isEditable) => (
        <Tag color={isEditable ? 'green' : 'red'}>
          {isEditable ? '是' : '否'}
        </Tag>
      ),
    },
    {
      title: '可见',
      dataIndex: 'isVisible',
      key: 'isVisible',
      width: 80,
      render: (isVisible) => (
        <Tag color={isVisible ? 'green' : 'red'}>
          {isVisible ? '是' : '否'}
        </Tag>
      ),
    },
    {
      title: '排序',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 80,
      render: (order) => order || 0,
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date) => dayjs(date).format('YYYY-MM-DD'),
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="查看详情">
            <Button 
              type="link" 
              icon={<EyeOutlined />}
              onClick={() => handleViewParameter(record)}
              size="small"
            />
          </Tooltip>
          {currentUser?.role === 'admin' && record.isEditable && (
            <Tooltip title="编辑参数">
              <Button 
                type="link" 
                icon={<EditOutlined />}
                onClick={() => handleEditParameter(record)}
                size="small"
              />
            </Tooltip>
          )}
          {currentUser?.role === 'admin' && record.isEditable && (
            <Popconfirm
              title="确定要删除这个系统参数吗？"
              description="删除后系统可能无法正常运行"
              onConfirm={() => handleDeleteParameter(record)}
              okText="确定"
              cancelText="取消"
            >
              <Tooltip title="删除参数">
                <Button 
                  type="link" 
                  icon={<DeleteOutlined />}
                  danger
                  size="small"
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="system-parameter-management">
      {/* 页面标题和操作 */}
      <Card 
        title={
          <Space>
            <SettingOutlined />
            系统参数设置
          </Space>
        }
        extra={
          <Space>
            <Search
              placeholder="搜索参数键或描述"
              allowClear
              style={{ width: 250 }}
              onSearch={setSearchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <Select
              placeholder="选择分类"
              allowClear
              style={{ width: 120 }}
              value={categoryFilter}
              onChange={setCategoryFilter}
            >
              {categoriesData?.categories?.map(category => (
                <Option key={category} value={category}>
                  {category}
                </Option>
              ))}
            </Select>
            <Select
              placeholder="可见性"
              allowClear
              style={{ width: 100 }}
              value={visibilityFilter}
              onChange={setVisibilityFilter}
            >
              <Option value="true">可见</Option>
              <Option value="false">隐藏</Option>
            </Select>
            
            {currentUser?.role === 'admin' && (
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={handleCreateParameter}
              >
                新增参数
              </Button>
            )}
          </Space>
        }
        className="status-card"
      >
        {/* 参数列表 */}
        <Table
          columns={columns}
          dataSource={parametersData?.parameters || []}
          rowKey="id"
          loading={isLoading}
          pagination={{
            total: parametersData?.pagination?.total || 0,
            pageSize: 50,
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 个参数`,
          }}
          scroll={{ x: 1400 }}
        />
      </Card>

      {/* 新增参数模态框 */}
      <Modal
        title="新增系统参数"
        open={isCreateModalVisible}
        onCancel={() => {
          setIsCreateModalVisible(false);
          createForm.resetFields();
        }}
        onOk={() => createForm.submit()}
        confirmLoading={createParameterMutation.isLoading}
        width={600}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreateSubmit}
        >
          <Form.Item
            name="paramKey"
            label="参数键"
            rules={[
              { required: true, message: '请输入参数键' },
              { pattern: /^[a-zA-Z0-9_.-]+$/, message: '参数键只能包含字母、数字、下划线、点和短横线' },
              { max: 100, message: '参数键长度不能超过100字符' }
            ]}
          >
            <Input placeholder="请输入参数键，如：system.title" />
          </Form.Item>
          
          <Form.Item
            name="paramValue"
            label="参数值"
            rules={[{ required: true, message: '请输入参数值' }]}
          >
            <TextArea 
              rows={3}
              placeholder="请输入参数值" 
            />
          </Form.Item>
          
          <Form.Item
            name="paramType"
            label="参数类型"
            rules={[{ required: true, message: '请选择参数类型' }]}
          >
            <Select placeholder="请选择参数类型">
              <Option value="string">字符串</Option>
              <Option value="number">数字</Option>
              <Option value="boolean">布尔值</Option>
              <Option value="json">JSON</Option>
              <Option value="array">数组</Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            name="category"
            label="分类"
            rules={[
              { required: true, message: '请输入分类' },
              { max: 50, message: '分类长度不能超过50字符' }
            ]}
          >
            <Input placeholder="请输入分类，如：系统设置" />
          </Form.Item>
          
          <Form.Item
            name="description"
            label="描述"
            rules={[{ max: 500, message: '描述长度不能超过500字符' }]}
          >
            <TextArea 
              rows={2}
              placeholder="请输入参数描述" 
            />
          </Form.Item>
          
          <Form.Item
            name="sortOrder"
            label="排序值"
            rules={[{ type: 'number', min: 0, message: '排序值必须为非负整数' }]}
          >
            <InputNumber 
              min={0}
              style={{ width: '100%' }}
              placeholder="请输入排序值，数字越小越靠前" 
            />
          </Form.Item>
          
          <Form.Item
            name="isEditable"
            label="可编辑"
            valuePropName="checked"
          >
            <Switch checkedChildren="是" unCheckedChildren="否" />
          </Form.Item>
          
          <Form.Item
            name="isVisible"
            label="可见"
            valuePropName="checked"
          >
            <Switch checkedChildren="是" unCheckedChildren="否" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑参数模态框 */}
      <Modal
        title="编辑系统参数"
        open={isEditModalVisible}
        onCancel={() => {
          setIsEditModalVisible(false);
          editForm.resetFields();
          setEditingParameter(null);
        }}
        onOk={() => editForm.submit()}
        confirmLoading={updateParameterMutation.isLoading}
        width={600}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditSubmit}
        >
          <Form.Item
            name="paramKey"
            label="参数键"
            rules={[
              { required: true, message: '请输入参数键' },
              { pattern: /^[a-zA-Z0-9_.-]+$/, message: '参数键只能包含字母、数字、下划线、点和短横线' },
              { max: 100, message: '参数键长度不能超过100字符' }
            ]}
          >
            <Input placeholder="请输入参数键，如：system.title" />
          </Form.Item>
          
          <Form.Item
            name="paramValue"
            label="参数值"
            rules={[{ required: true, message: '请输入参数值' }]}
          >
            <TextArea 
              rows={3}
              placeholder="请输入参数值" 
            />
          </Form.Item>
          
          <Form.Item
            name="paramType"
            label="参数类型"
            rules={[{ required: true, message: '请选择参数类型' }]}
          >
            <Select placeholder="请选择参数类型">
              <Option value="string">字符串</Option>
              <Option value="number">数字</Option>
              <Option value="boolean">布尔值</Option>
              <Option value="json">JSON</Option>
              <Option value="array">数组</Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            name="category"
            label="分类"
            rules={[
              { required: true, message: '请输入分类' },
              { max: 50, message: '分类长度不能超过50字符' }
            ]}
          >
            <Input placeholder="请输入分类，如：系统设置" />
          </Form.Item>
          
          <Form.Item
            name="description"
            label="描述"
            rules={[{ max: 500, message: '描述长度不能超过500字符' }]}
          >
            <TextArea 
              rows={2}
              placeholder="请输入参数描述" 
            />
          </Form.Item>
          
          <Form.Item
            name="sortOrder"
            label="排序值"
            rules={[{ type: 'number', min: 0, message: '排序值必须为非负整数' }]}
          >
            <InputNumber 
              min={0}
              style={{ width: '100%' }}
              placeholder="请输入排序值，数字越小越靠前" 
            />
          </Form.Item>
          
          <Form.Item
            name="isEditable"
            label="可编辑"
            valuePropName="checked"
          >
            <Switch checkedChildren="是" unCheckedChildren="否" />
          </Form.Item>
          
          <Form.Item
            name="isVisible"
            label="可见"
            valuePropName="checked"
          >
            <Switch checkedChildren="是" unCheckedChildren="否" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 参数详情模态框 */}
      <Modal
        title="系统参数详情"
        open={isDetailModalVisible}
        onCancel={() => setIsDetailModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedParameter && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="参数键">
              <Text code copyable={{ text: selectedParameter.paramKey }}>
                {selectedParameter.paramKey}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="参数值">
              <div style={{ maxHeight: 200, overflow: 'auto' }}>
                <Text 
                  copyable={{ text: selectedParameter.paramValue }}
                  style={{ whiteSpace: 'pre-wrap' }}
                >
                  {formatParameterValue(selectedParameter.paramValue, selectedParameter.paramType)}
                </Text>
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="参数类型">
              <Tag color={getTypeColor(selectedParameter.paramType)}>
                {getTypeText(selectedParameter.paramType)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="分类">
              <Tag color="blue">{selectedParameter.category}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="描述">
              {selectedParameter.description || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="可编辑">
              <Tag color={selectedParameter.isEditable ? 'green' : 'red'}>
                {selectedParameter.isEditable ? '是' : '否'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="可见">
              <Tag color={selectedParameter.isVisible ? 'green' : 'red'}>
                {selectedParameter.isVisible ? '是' : '否'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="排序值">
              {selectedParameter.sortOrder || 0}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {dayjs(selectedParameter.createdAt).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="更新时间">
              {dayjs(selectedParameter.updatedAt).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="创建者">
              {selectedParameter.createdBy || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="更新者">
              {selectedParameter.updatedBy || '-'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default SystemParameterManagement;